<script setup lang="ts">

import OpenInNewTabIcon from '@/components/OpensInNewTabIcon.vue'
import LibrarySaveChangesButton from '@/components/LibrarySaveChangesButton.vue'
import FolderAvatar from '@/library/FolderAvatar.vue'
import { computed, PropType, ref } from 'vue'
import { timeAgo } from '@/util/time'
import { useLibraryStore } from '@/library/library.store.ts'
import { SavingState } from '@/presentlevels/presentlevels_types.ts'
import router from '@/router'
import { useConfirm } from 'primevue/useconfirm'
import { storeToRefs } from 'pinia'
import { CONTENT_TYPES } from '@/util/access_visibility'
import { LibraryContentObject } from '@/library/library_types.ts'
import CopySummaryModal from '@/presentlevels/CopySummaryModal.vue'
import CopyImpactOfDisabilityModal from '@/impact-of-disability/CopyImpactOfDisabilityModal.vue'
import TextLinkButton from '@/components/TextLinkButton.vue'
import FolderIcon from '@/library/FolderIcon.vue'

const props = defineProps({
  contentUuid: {
    type: String,
    required: true
  },
  saveStatus: {
    type: String as PropType<SavingState>,
    required: true
  },
  updatedAt: {
    type: String,
  },
  contentType: {
    type: String,
    required: true
  }
})

const libraryStore = useLibraryStore()
const confirm = useConfirm()

const emit = defineEmits<{
  save: []
  discard: []
  remove: []
}>()

const { currentFolder } = storeToRefs(libraryStore)
const openCopySummaryModal = ref(false)

const formattedLastUpdated = computed(() => {
  return props.updatedAt ? timeAgo(props.updatedAt) : ''
})

const currentContent = computed(() => {
  let contentList: LibraryContentObject[] = []
  switch (props.contentType) {
    case CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY:
      contentList = libraryStore.presentlevelStudentSummaries
      break;
    case CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY:
      contentList = libraryStore.impactsOfDisabilities
      break;
    default:
      console.warn(`unsupported content type:${props.contentType}`)
  }
  return contentList.find(c => c.content_uuid === props.contentUuid)
})

const contributorUUID = computed(() => {
  if (!currentContent.value) {
    return null
  }
  return currentContent.value.contributor
})

const contentOwnerUuid = computed(() => {
  if (libraryStore.currentFolderOwner) {
    return libraryStore.currentFolderOwner
  }
  return contributorUUID.value
})

const shouldShowContributor = computed(() => {
  if (!contributorUUID.value || !libraryStore.currentFolderOwner) {
    return false
  }
  return contentOwnerUuid.value !== contributorUUID.value
})

const folderDisplayName = computed(() => {
  const maxTitleLength = 30
  if (!currentFolder.value) {
    return ''
  }
  const name = currentFolder.value.name
  if (name.length < maxTitleLength) {
    return name
  }
  return `${name.slice(0,maxTitleLength)}...`
})

function copyButtonLabel() {
  let contentLabel = 'content'
  switch (props.contentType) {
    case CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY:
    case CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY:
      contentLabel = 'statement'
      break
  }
  return `Copy ${contentLabel} to another folder`
}

const removeFromFolder = () => {
  if (!currentFolder.value?.uuid) {
    return
  }
  
  let contentLabel = 'content'
  let headerLabel = 'Content'
  switch (props.contentType) {
    case CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY:
      contentLabel = 'present levels statement'
      headerLabel = 'Present Levels Statement'
      break;
    case CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY:
      contentLabel = 'Impact of Disability statement'
      headerLabel = 'Impact of Disability Statement'
      break;
  }
  
  confirm.require({
    group: `${props.contentUuid}_${currentFolder.value.uuid}`,
    message: `Are you sure you want to delete this ${contentLabel} from this folder? This action is irreversible.`,
    header: `Delete ${headerLabel}`,
    accept: async () => {      
      await libraryStore.deleteResource({
        uuid: props.contentUuid,
        type: props.contentType,
        collection: currentFolder.value.uuid
      })
      emit('remove')
      router.push({
        name: 'LibraryFolder',
        params: { uuid: currentFolder.value?.uuid }
      })
    }
  })
}
</script>

<template>
  <div class="bg-green-100 rounded-sm border border-gray-400 p-4">
    <div class="flex justify-between items-center mb-2">
      <div class="flex items-center font-bold text-green-300 gap-1">
        <FolderIcon :is-shared="libraryStore.isCurrentFolderShared" color-class="text-green-300" size-class="text-xl" />
        <router-link
          v-if="currentFolder.uuid"
          :to="{ name: 'LibraryFolder', params: { uuid: currentFolder.uuid } }"
          class="text-xl"
          target="_blank"
          rel="noopener"
          :aria-label="`Open ${currentFolder.name} folder in a new tab`"
        >
          <span data-testid="folder-name" class="mr-1 hover:underline fs-exclude">{{ folderDisplayName }}</span>
          <OpenInNewTabIcon />
        </router-link>
      </div>
    </div>

    <div class="flex items-center text-gray-900 mb-2">
      <span class="material-icons-round mr-1 text-text-md">calendar_month</span>
      <span class="text-text-sm">Saved {{ formattedLastUpdated }}</span>
    </div>

    <div v-if="libraryStore.isCurrentFolderShared" class="flex items-center gap-1 mb-4 text-sm">
      <template v-if="libraryStore.currentFolderOwner">
        <span class="text-gray-900">Owner:</span>
        <FolderAvatar :user-id="libraryStore.currentFolderOwner" :folder="currentFolder" avatar-size="sm" />
      </template>
      <template v-if="shouldShowContributor">
        <span class="text-gray-900 ml-3">Contributed by:</span>
        <FolderAvatar :user-id="contributorUUID || ''" :folder="currentFolder" avatar-size="sm" />
      </template>
    </div>

    <template v-if="libraryStore.doesUserOwnCurrentFolder">
      <LibrarySaveChangesButton
        :save-status="saveStatus"
        @save="$emit('save')"
        @discard="$emit('discard')"
      />
    </template>
    <template v-else>
      <div class="flex items-center p-1 bg-white border rounded-sm mb-4">
        <span class="material-icons text-gray-900 text-sm mr-1">lock</span>
        <span class="text-sm text-gray-900 italic">Only the owner of this Shared Folder can make edits.</span>
      </div>
    </template>

    <div class="mt-4 flex flex-col gap-2">
      <TextLinkButton
        :text="copyButtonLabel()"
        icon-name="content_copy"
        @click="openCopySummaryModal = true"
      />

      <TextLinkButton
        v-if="libraryStore.canDeleteCurrentFolderContent"
        color="red"
        text="Remove from folder"
        icon-name="delete"
        @click="removeFromFolder"
      />
    </div>

  </div>
  <CopySummaryModal
    v-if="props.contentType === CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY"
    :visible="openCopySummaryModal"
    :content-uuid="props.contentUuid"
    @update:visible="(val: boolean) => { openCopySummaryModal = val }"
  />
  <CopyImpactOfDisabilityModal
    v-if="props.contentType === CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY"
    :visible="openCopySummaryModal"
    :content-uuid="props.contentUuid"
    @update:visible="(val: boolean) => { openCopySummaryModal = val }"
  />
</template>

<style scoped>

</style>